/**
 * Location tracks as map features (design §9): sections coloured by source,
 * start/stop/end places, and each source's position at the playhead.
 *
 * @module
 */
import type {
  Feature as GeoFeature,
  FeatureCollection,
  Geometry,
} from "geojson";
import type { Schemas } from "../api/client";

export type LocationTrack = Schemas["LocationTrack"];

/** Fixes further apart than this aren't joined (as in avgv-geo). */
const GAP_MS = 60_000;

/** Distinct, readable on light and dark maps. */
export const SOURCE_COLOURS = [
  "#1f77b4",
  "#d62728",
  "#2ca02c",
  "#9467bd",
  "#ff7f0e",
  "#17becf",
  "#e377c2",
  "#8c564b",
];

export interface SourceTrack {
  key: string;
  name: string;
  colour: string;
  /** In time order; files from one source are joined. */
  tracks: LocationTrack[];
}

type Feature = GeoFeature<Geometry, Record<string, string | number | boolean>>;

/** One LineString per section, tagged with its source, colour and kind. */
export function sectionFeatures(
  sources: readonly SourceTrack[],
): FeatureCollection {
  const features: Feature[] = [];
  for (const s of sources) {
    for (const t of s.tracks) {
      for (const seg of t.segments ?? []) {
        const coords = t.fixes
          .slice(seg.first, seg.last + 1)
          .map((f) => [f.lon, f.lat]);
        if (coords.length < 2) continue;
        features.push({
          type: "Feature",
          geometry: { type: "LineString", coordinates: coords },
          properties: {
            source: s.key,
            name: s.name,
            colour: s.colour,
            kind: seg.kind,
          },
        });
      }
    }
  }
  return { type: "FeatureCollection", features };
}

/** Start, end and numbered stops (per source, in time order). */
export function placeFeatures(
  sources: readonly SourceTrack[],
): FeatureCollection {
  const features: Feature[] = [];
  for (const s of sources) {
    const places = s.tracks
      .flatMap((t) => t.places ?? [])
      .sort((a, b) => a.from_ms - b.from_ms);
    const first = places.find((p) => p.kind === "track_start");
    const last = [...places].reverse().find((p) => p.kind === "track_end");
    let stop = 0;
    for (const p of places) {
      if (p.kind === "stop") stop += 1;
      else if (p !== first && p !== last) continue;
      features.push({
        type: "Feature",
        geometry: { type: "Point", coordinates: [p.lon, p.lat] },
        properties: {
          source: s.key,
          name: s.name,
          colour: s.colour,
          kind: p.kind,
          label: p.kind === "stop" ? String(stop) : "",
          from_ms: p.from_ms,
          to_ms: p.to_ms,
        },
      });
    }
  }
  return { type: "FeatureCollection", features };
}

/** Most points the heat map draws; beyond it, fixes are taken evenly. */
const HEAT_MAX_POINTS = 50_000;

/** Every fix of every source, for a heat map of where they were most. */
export function heatFeatures(sources: readonly SourceTrack[]): FeatureCollection {
  const fixes = sources.flatMap((s) => s.tracks.flatMap((t) => t.fixes));
  const step = Math.max(1, Math.ceil(fixes.length / HEAT_MAX_POINTS));
  const features: Feature[] = [];
  for (let i = 0; i < fixes.length; i += step) {
    const f = fixes[i];
    if (!f) continue;
    features.push({
      type: "Feature",
      geometry: { type: "Point", coordinates: [f.lon, f.lat] },
      properties: {},
    });
  }
  return { type: "FeatureCollection", features };
}

/** Named places the files give (GPX waypoints), once each. */
export function waypointFeatures(sources: readonly SourceTrack[]): FeatureCollection {
  const seen = new Set<string>();
  const features: Feature[] = [];
  for (const s of sources)
    for (const t of s.tracks)
      for (const w of t.waypoints ?? []) {
        const key = `${w.name}|${w.lat.toFixed(5)}|${w.lon.toFixed(5)}`;
        if (seen.has(key)) continue;
        seen.add(key);
        features.push({
          type: "Feature",
          geometry: { type: "Point", coordinates: [w.lon, w.lat] },
          properties: { name: w.name, source: s.name },
        });
      }
  return { type: "FeatureCollection", features };
}

/** Where the track is at `t`: between two fixes, linearly; none in a gap or outside it. */
export function positionAt(
  track: LocationTrack,
  t: number,
): [number, number] | null {
  const f = track.fixes;
  if (f.length === 0) return null;
  let lo = 0;
  let hi = f.length - 1;
  const first = f[lo];
  const last = f[hi];
  if (!first || !last || t < first.t_ms || t > last.t_ms) return null;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if ((f[mid]?.t_ms ?? 0) <= t) lo = mid;
    else hi = mid;
  }
  const a = f[lo];
  const b = f[hi];
  if (!a || !b) return null;
  if (t === a.t_ms) return [a.lon, a.lat];
  if (t === b.t_ms) return [b.lon, b.lat];
  if (b.t_ms - a.t_ms > GAP_MS) return null;
  const k = (t - a.t_ms) / (b.t_ms - a.t_ms);
  return [a.lon + (b.lon - a.lon) * k, a.lat + (b.lat - a.lat) * k];
}

export function positionFeatures(
  sources: readonly SourceTrack[],
  t: number,
): FeatureCollection {
  const features: Feature[] = [];
  for (const s of sources) {
    for (const track of s.tracks) {
      const p = positionAt(track, t);
      if (!p) continue;
      features.push({
        type: "Feature",
        geometry: { type: "Point", coordinates: p },
        properties: { source: s.key, name: s.name, colour: s.colour },
      });
      break;
    }
  }
  return { type: "FeatureCollection", features };
}

/** West, south, east, north of every fix, or null if there are none. */
export function trackBounds(
  sources: readonly SourceTrack[],
): [number, number, number, number] | null {
  let b: [number, number, number, number] | null = null;
  for (const s of sources)
    for (const t of s.tracks)
      for (const f of t.fixes) {
        b = b
          ? [
              Math.min(b[0], f.lon),
              Math.min(b[1], f.lat),
              Math.max(b[2], f.lon),
              Math.max(b[3], f.lat),
            ]
          : [f.lon, f.lat, f.lon, f.lat];
      }
  return b;
}

/** The fix of `source` nearest `[lon, lat]`, measured on a local flat projection. */
export function nearestFix(
  source: SourceTrack,
  lon: number,
  lat: number,
): LocationTrack["fixes"][number] | null {
  const k = Math.cos((lat * Math.PI) / 180);
  let best: LocationTrack["fixes"][number] | null = null;
  let bestD = Infinity;
  for (const t of source.tracks)
    for (const f of t.fixes) {
      const d = ((f.lon - lon) * k) ** 2 + (f.lat - lat) ** 2;
      if (d < bestD) {
        bestD = d;
        best = f;
      }
    }
  return best;
}

/** The parts of each track between `from` and `to`, split at gaps. */
export function spanFeatures(
  sources: readonly SourceTrack[],
  from: number,
  to: number,
): FeatureCollection {
  const features: Feature[] = [];
  for (const s of sources)
    for (const t of s.tracks) {
      let run: number[][] = [];
      let prev = -Infinity;
      const flush = () => {
        if (run.length >= 2)
          features.push({
            type: "Feature",
            geometry: { type: "LineString", coordinates: run },
            properties: { source: s.key, colour: s.colour },
          });
        run = [];
      };
      for (const f of t.fixes) {
        if (f.t_ms < from || f.t_ms > to || f.t_ms - prev > GAP_MS) flush();
        if (f.t_ms >= from && f.t_ms <= to) run.push([f.lon, f.lat]);
        prev = f.t_ms;
      }
      flush();
    }
  return { type: "FeatureCollection", features };
}

export interface Interval {
  from: number;
  to: number;
}

/**
 * When each source was inside `[west, south, east, north]`: runs of fixes
 * inside it, split at gaps, each widened to the fixes either side so a brief
 * visit still counts.
 */
export function insideIntervals(
  sources: readonly SourceTrack[],
  [w, s, e, n]: readonly [number, number, number, number],
): Map<string, Interval[]> {
  const out = new Map<string, Interval[]>();
  for (const src of sources) {
    const list: Interval[] = [];
    for (const t of src.tracks) {
      const f = t.fixes;
      let open: Interval | null = null;
      f.forEach((fix, i) => {
        const inside =
          fix.lon >= w && fix.lon <= e && fix.lat >= s && fix.lat <= n;
        const before = f[i - 1];
        const after = f[i + 1];
        const joined = before !== undefined && fix.t_ms - before.t_ms <= GAP_MS;
        if (inside) {
          if (open && joined) open.to = fix.t_ms;
          else {
            if (open) list.push(open);
            open = {
              from: joined && before ? before.t_ms : fix.t_ms,
              to: fix.t_ms,
            };
          }
          if (after && after.t_ms - fix.t_ms <= GAP_MS) open.to = after.t_ms;
        } else if (open && open.to < fix.t_ms) {
          list.push(open);
          open = null;
        }
      });
      if (open) list.push(open);
    }
    if (list.length > 0) out.set(src.key, list);
  }
  return out;
}
